"use client";

import { useFormatter, useTranslations } from "next-intl";
import React, { useId } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export type TrendPoint = {
  month: string;
  avg_price: number | null;
  n_listings: number;
};

type Props = {
  data: TrendPoint[];
};

const monthFormatter = (iso: string) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

const PriceTrendChart: React.FC<Props> = ({ data }) => {
  const gradId = useId();
  const t = useTranslations("listing");
  const formatter = useFormatter();

  if (!data.length) return null;

  const formatPrice = (v: number) =>
    formatter.number(v, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });
  return (
    <div className="">
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart
          data={data}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            stroke="currentColor"
            strokeOpacity={0.08}
            vertical={false}
          />

          <XAxis
            dataKey="month"
            tick={{ fill: "currentColor", fontSize: 11, opacity: 0.7 }}
            tickLine={false}
            axisLine={{ stroke: "currentColor", strokeOpacity: 0.1 }}
            minTickGap={24}
            tickFormatter={monthFormatter}
          />
          <YAxis
            tick={{ fill: "currentColor", fontSize: 11, opacity: 0.7 }}
            tickFormatter={(v) => formatPrice(Number(v))}
            tickLine={false}
            axisLine={{ stroke: "currentColor", strokeOpacity: 0.1 }}
            width={56}
          />
          <Tooltip
            labelStyle={{ color: "inherit", opacity: 0.8, fontSize: 14 }}
            contentStyle={{
              background: "rgba(24,24,27,0.92)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              color: "white",
              fontSize: 14,
            }}
            cursor={{ stroke: "currentColor", strokeOpacity: 0.15 }}
            labelFormatter={(value: string) => {
              const date = new Date(value);
              return date.toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
              });
            }}
            formatter={(value: any, name: any, props: any) => {
              if (name === "avg_price") {
                const row = props?.payload;
                return [
                  formatPrice(value),
                  t("price-trends.chart.tootltip", {
                    listings: row?.n_listings ?? 0,
                  }),
                ];
              }
              return value;
            }}
          />

          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.35} />
              <stop offset="60%" stopColor="currentColor" stopOpacity={0.12} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="avg_price"
            stroke="currentColor"
            strokeOpacity={0.9}
            strokeWidth={2.2}
            fill={`url(#${gradId})`}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive
            animationDuration={600}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PriceTrendChart;
